import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface ModalState {
  isOpen: boolean;
  type: 'alert' | 'confirm';
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  icon?: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
}

interface AppModalContextType {
  showAlert: (message: string, options?: { title?: string; icon?: string; variant?: ModalState['variant'] }) => Promise<void>;
  showConfirm: (message: string, options?: { title?: string; confirmText?: string; cancelText?: string; icon?: string; variant?: ModalState['variant'] }) => Promise<boolean>;
}

const AppModalContext = createContext<AppModalContextType | null>(null);

const variantStyles: Record<string, { bg: string; iconBg: string; btn: string }> = {
  success: { bg: 'from-green-50 to-emerald-50', iconBg: 'bg-green-100', btn: 'bg-green-500 hover:bg-green-600' },
  error: { bg: 'from-red-50 to-rose-50', iconBg: 'bg-red-100', btn: 'bg-red-500 hover:bg-red-600' },
  warning: { bg: 'from-amber-50 to-yellow-50', iconBg: 'bg-amber-100', btn: 'bg-amber-500 hover:bg-amber-600' },
  info: { bg: 'from-blue-50 to-indigo-50', iconBg: 'bg-blue-100', btn: 'bg-primary-500 hover:bg-primary-600' },
};

const defaultIcons: Record<string, string> = {
  success: '✅',
  error: '❌',
  warning: '⚠️',
  info: 'ℹ️',
};

export const AppModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    type: 'alert',
    title: '',
    message: '',
  });

  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const showAlert = useCallback((message: string, options?: { title?: string; icon?: string; variant?: ModalState['variant'] }) => {
    return new Promise<void>((resolve) => {
      resolveRef.current = () => resolve();
      setModal({
        isOpen: true,
        type: 'alert',
        title: options?.title || '알림',
        message,
        icon: options?.icon,
        variant: options?.variant || 'info',
      });
    });
  }, []);

  const showConfirm = useCallback((message: string, options?: { title?: string; confirmText?: string; cancelText?: string; icon?: string; variant?: ModalState['variant'] }) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setModal({
        isOpen: true,
        type: 'confirm',
        title: options?.title || '확인',
        message,
        confirmText: options?.confirmText || '확인',
        cancelText: options?.cancelText || '취소',
        icon: options?.icon,
        variant: options?.variant || 'warning',
      });
    });
  }, []);

  const handleClose = useCallback((result: boolean) => {
    setModal(prev => ({ ...prev, isOpen: false }));
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  }, []);

  const style = variantStyles[modal.variant || 'info'];
  const icon = modal.icon || defaultIcons[modal.variant || 'info'];

  return (
    <AppModalContext.Provider value={{ showAlert, showConfirm }}>
      {children}

      {/* Modal overlay */}
      {modal.isOpen && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => handleClose(false)} />

          {/* Modal card */}
          <div
            className={`relative w-full max-w-[340px] bg-gradient-to-b ${style.bg} rounded-2xl shadow-xl overflow-hidden`}
            style={{ animation: 'slideUp 0.2s ease-out' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-white/80 backdrop-blur-sm p-6 flex flex-col items-center text-center">
              {/* Icon */}
              <div className={`w-12 h-12 ${style.iconBg} rounded-full flex items-center justify-center text-2xl mb-3`}>
                {icon}
              </div>

              {/* Title */}
              <h3 className="text-[16px] font-bold text-achromatic-900 mb-2">{modal.title}</h3>

              {/* Message */}
              <p className="text-[13px] text-achromatic-500 leading-relaxed whitespace-pre-line">{modal.message}</p>

              {/* Actions */}
              <div className="flex gap-2 mt-5 w-full">
                {modal.type === 'confirm' && (
                  <button
                    onClick={() => handleClose(false)}
                    className="flex-1 py-2.5 rounded-xl text-[13px] font-bold text-achromatic-500 bg-achromatic-100 hover:bg-achromatic-200 transition-colors active:scale-[0.98]"
                  >
                    {modal.cancelText}
                  </button>
                )}
                <button
                  onClick={() => handleClose(true)}
                  className={`flex-1 py-2.5 rounded-xl text-[13px] font-bold text-white ${style.btn} transition-colors active:scale-[0.98]`}
                >
                  {modal.type === 'confirm' ? modal.confirmText : '확인'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </AppModalContext.Provider>
  );
};

export const useAppModal = (): AppModalContextType => {
  const ctx = useContext(AppModalContext);
  if (!ctx) throw new Error('useAppModal must be used within AppModalProvider');
  return ctx;
};

export default AppModalProvider;
